import { addWorkingDays, workingDaysBetween, snapForward, snapBack } from "./calendar.js";

/* The critical path method, on the rows as they stand.
 *
 * Forward pass for the earliest anything can happen, backward pass for the
 * latest it can happen without pushing the end date, and the difference is
 * float. An activity with no float is on the critical path: miss a day on it
 * and the program finishes a day later.
 *
 * Nothing here is stored. Float is a number that was true at the moment it was
 * computed, and a stored one is a number that was true once. It is cheap:
 * 555 activities and 205 links resolve in about a millisecond, which is less
 * time than it takes to fetch them.
 *
 * Two conventions, both the ones every scheduling tool uses.
 *
 * Dates are inclusive. An activity that starts and finishes on the same
 * Tuesday is one day long. So finish = start + duration - 1 working days, and
 * a successor starts the working day AFTER its predecessor finishes.
 *
 * Summary rows are not scheduled. Their dates are the span of their children,
 * computed after the fact. A summary bar that is typed rather than derived is
 * exactly how the WAPA plan ended up with fourteen headers disagreeing with
 * the rows underneath them.
 */

const LATEST = (a, b) => (!a ? b : !b ? a : a > b ? a : b);
const EARLIEST = (a, b) => (!a ? b : !b ? a : a < b ? a : b);

/* Duration in working days. Given explicitly where the plan says so,
   otherwise read off the dates. A milestone is zero by definition. */
export function durationOf(activity) {
  if (activity.is_milestone) return 0;
  if (Number.isFinite(activity.duration_days)) return Math.max(0, activity.duration_days);
  const d = workingDaysBetween(activity.start_date, activity.finish_date);
  return d === null ? 1 : Math.max(1, d);
}

/* Order the activities so every predecessor comes before its successors.
 * Returns null where the logic contains a loop, because a loop has no such
 * order and the honest answer is to say so rather than to schedule something
 * arbitrary. Kahn's algorithm: whatever is left when nothing has zero
 * remaining predecessors is the cycle. */
export function topologicalOrder(ids, links) {
  const waiting = new Map(ids.map((id) => [id, 0]));
  const after = new Map(ids.map((id) => [id, []]));

  for (const l of links) {
    if (!waiting.has(l.from) || !waiting.has(l.to)) continue;
    after.get(l.from).push(l.to);
    waiting.set(l.to, waiting.get(l.to) + 1);
  }

  const ready = ids.filter((id) => waiting.get(id) === 0);
  const order = [];
  while (ready.length) {
    const id = ready.shift();
    order.push(id);
    for (const next of after.get(id)) {
      waiting.set(next, waiting.get(next) - 1);
      if (waiting.get(next) === 0) ready.push(next);
    }
  }

  return order.length === ids.length
    ? { order, cycle: [] }
    : { order: null, cycle: ids.filter((id) => waiting.get(id) > 0) };
}

/* Where a successor may start or finish, given one predecessor.
 * FS is the ordinary one: start the working day after. SS and FF align the
 * same ends. SF is rare and is here for completeness rather than because
 * either WAPA file uses it. */
function constraintFrom(link, pred) {
  const lag = link.lag_days ?? 0;
  switch (link.link_type) {
    case "SS": return { start: addWorkingDays(pred.early_start, lag) };
    case "FF": return { finish: addWorkingDays(pred.early_finish, lag) };
    case "SF": return { finish: addWorkingDays(pred.early_start, lag) };
    default:   return { start: addWorkingDays(pred.early_finish, 1 + lag) };
  }
}

function backConstraintFrom(link, succ, duration) {
  const lag = link.lag_days ?? 0;
  switch (link.link_type) {
    /* The successor's late start bounds the predecessor's late start. */
    case "SS": return { start: addWorkingDays(succ.late_start, -lag) };
    case "FF": return { finish: addWorkingDays(succ.late_finish, -lag) };
    case "SF": return { start: addWorkingDays(succ.late_finish, -lag) };
    default:   return { finish: addWorkingDays(succ.late_start, -(1 + lag)) };
  }
}

/* The whole calculation. Activities in, activities out with six more fields
 * on each and nothing changed on the originals. */
export function computeSchedule(activities = [], links = [], { asOf } = {}) {
  const work = activities.filter((a) => !a.is_summary);
  const ids = work.map((a) => a.id);
  const byId = new Map(work.map((a) => [a, a]).map(([a]) => [a.id, { ...a }]));

  const live = links.filter((l) => byId.has(l.from) && byId.has(l.to));
  const { order, cycle } = topologicalOrder(ids, live);

  /* A loop in the logic. Every date is left as it was entered and the caller
     is told which activities are in it, because a schedule that quietly
     invents dates around a circular dependency is worse than one that
     refuses. */
  if (!order) {
    return {
      activities: [...byId.values()].map((a) => ({
        ...a, early_start: a.start_date, early_finish: a.finish_date,
        late_start: null, late_finish: null, total_float: null, is_critical: false,
      })),
      cycle,
      ok: false,
    };
  }

  const into = new Map(ids.map((id) => [id, []]));
  const outOf = new Map(ids.map((id) => [id, []]));
  for (const l of live) { into.get(l.to).push(l); outOf.get(l.from).push(l); }

  /* ---- forward pass */
  for (const id of order) {
    const a = byId.get(id);
    const duration = durationOf(a);

    /* An activity with predecessors is driven by them. Its typed start date
       is an output of the last calculation, not an input to this one, and
       treating it as a floor is what stops a start to start link from ever
       pulling anything earlier.
       An activity with no predecessors keeps its date, because two thirds of
       the WAPA plan has no logic at all and collapsing all of it onto the
       project start would produce a confident, useless answer. */
    const driven = into.get(id).length > 0;
    let start = !driven && a.start_date ? snapForward(a.start_date) : null;
    let finish = null;

    for (const l of into.get(id)) {
      const c = constraintFrom(l, byId.get(l.from));
      if (c.start) start = LATEST(start, c.start);
      if (c.finish) finish = LATEST(finish, c.finish);
    }

    /* An FF or SF link bounds the finish, so the start follows from it. */
    if (finish && (!start || addWorkingDays(finish, -Math.max(0, duration - 1)) > start)) {
      start = addWorkingDays(finish, -Math.max(0, duration - 1));
    }
    if (!start) start = snapForward(a.start_date ?? a.finish_date ?? "2000-01-01");

    a.early_start = start;
    a.early_finish = LATEST(addWorkingDays(start, Math.max(0, duration - 1)), finish);
  }

  /* ---- backward pass, from the latest finish in the plan */
  const projectFinish = [...byId.values()]
    .reduce((latest, a) => LATEST(latest, a.early_finish), null);

  for (const id of [...order].reverse()) {
    const a = byId.get(id);
    const duration = durationOf(a);

    let lateFinish = null;
    let lateStart = null;

    for (const l of outOf.get(id)) {
      const c = backConstraintFrom(l, byId.get(l.to), duration);
      if (c.finish) lateFinish = EARLIEST(lateFinish, c.finish);
      if (c.start) lateStart = EARLIEST(lateStart, c.start);
    }

    /* Nothing depends on it, so it may finish as late as the program does. */
    if (!lateFinish && !lateStart) lateFinish = projectFinish;
    if (lateStart && !lateFinish) {
      lateFinish = addWorkingDays(lateStart, Math.max(0, duration - 1));
    }

    a.late_finish = snapBack(lateFinish);
    a.late_start = addWorkingDays(a.late_finish, -Math.max(0, duration - 1));
    if (lateStart) a.late_start = EARLIEST(a.late_start, lateStart);

    /* Float is how many working days it can slip before it pushes the end.
       workingDaysBetween counts inclusively, so the same day is 1 and the
       slack is one less than that. Without the subtraction every activity
       looks a day more comfortable than it is, which is the wrong direction
       to be wrong in. */
    a.total_float = a.late_start >= a.early_start
      ? workingDaysBetween(a.early_start, a.late_start) - 1
      : -(workingDaysBetween(a.late_start, a.early_start) - 1);
    a.is_critical = (a.total_float ?? 0) <= 0;
  }

  return { activities: [...byId.values()], cycle: [], ok: true, projectFinish, asOf };
}

/* Summary rows are the span of everything under them, computed rather than
 * typed. Fourteen headers in the WAPA plan disagree with their own children,
 * one of them by three hundred days, which is what typing them produces. */
export function rollUp(activities = []) {
  const byId = new Map(activities.map((a) => [a.id, { ...a }]));
  const children = new Map();
  for (const a of activities) {
    if (!a.parent_id) continue;
    if (!children.has(a.parent_id)) children.set(a.parent_id, []);
    children.get(a.parent_id).push(a.id);
  }

  /* Deepest first, so a summary of summaries is right by the time it is
     reached rather than reading its children before they are computed. */
  const depth = (a) => {
    let n = 0;
    let at = a;
    while (at?.parent_id && byId.has(at.parent_id)) { n += 1; at = byId.get(at.parent_id); }
    return n;
  };
  const order = [...byId.values()].sort((a, b) => depth(b) - depth(a));

  for (const a of order) {
    const kids = (children.get(a.id) ?? []).map((id) => byId.get(id)).filter(Boolean);
    if (!kids.length) continue;
    a.start_date = kids.map((k) => k.start_date).filter(Boolean).sort()[0] ?? a.start_date;
    a.finish_date = kids.map((k) => k.finish_date).filter(Boolean).sort().at(-1) ?? a.finish_date;
    a.early_start = kids.map((k) => k.early_start).filter(Boolean).sort()[0] ?? a.early_start;
    a.early_finish = kids.map((k) => k.early_finish).filter(Boolean).sort().at(-1) ?? a.early_finish;
    a.is_critical = kids.some((k) => k.is_critical);
  }

  return [...byId.values()];
}

/* What a header claims against what its rows say. Reported rather than
 * silently corrected, because the disagreement is the finding. */
export function headerDrift(activities = []) {
  const rolled = new Map(rollUp(activities).map((a) => [a.id, a]));
  const out = [];
  for (const a of activities) {
    if (!a.is_summary) continue;
    const truth = rolled.get(a.id);
    if (!truth) continue;
    const startBy = a.start_date && truth.start_date
      ? workingDaysBetween(truth.start_date, a.start_date) - 1 : 0;
    const finishBy = a.finish_date && truth.finish_date
      ? workingDaysBetween(a.finish_date, truth.finish_date) - 1 : 0;
    if (startBy > 0 || finishBy > 0) {
      out.push({
        id: a.id, title: a.title,
        claimed: { start: a.start_date, finish: a.finish_date },
        actual: { start: truth.start_date, finish: truth.finish_date },
        startsLateBy: Math.max(0, startBy),
        finishesEarlyBy: Math.max(0, finishBy),
      });
    }
  }
  return out.sort((a, b) =>
    (b.startsLateBy + b.finishesEarlyBy) - (a.startsLateBy + a.finishesEarlyBy));
}
